import React, { useState } from 'react';
import { BelongingHeartIcon, RemoveXIcon, ConfirmCheckIcon } from './CustomIcons';

/**
 * Floating Contact Orb - Gentle way to reach M.ai.ry's colleagues
 * Connects to Belonging Sessions message system
 */
const ContactOrb = ({ variant = 'terminal' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact: '', // email or phone
    contactType: 'email', // 'email' or 'phone'
    message: '',
    newsletter: false // Newsletter signup
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const BELONGING_API = process.env.REACT_APP_BELONGING_API || 'https://futuremind-chat.preview.emergentagent.com/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.contact || !formData.message) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Send message to Belonging Sessions
      const messageResponse = await fetch(`${BELONGING_API}/messages/from-mairy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          contact: formData.contact,
          contact_type: formData.contactType,
          message: formData.message,
          source: 'mairy.io',
          timestamp: new Date().toISOString()
        })
      });

      if (!messageResponse.ok) {
        throw new Error('Message failed to send');
      }

      // If newsletter checked, register them
      if (formData.newsletter && formData.contactType === 'email') {
        try {
          await fetch(`${BELONGING_API}/newsletter/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: formData.contact,
              name: formData.name,
              source: 'mairy.io',
              timestamp: new Date().toISOString()
            })
          });
          console.log('✓ Newsletter signup submitted');
        } catch (newsletterError) {
          console.error('Newsletter signup error:', newsletterError);
          // Don't fail the whole submission if newsletter fails
        }
      }

      // Success!
      setSubmitted(true);
      
      // Reset and close after showing success
      setTimeout(() => {
        setIsOpen(false);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', contact: '', contactType: 'email', message: '', newsletter: false });
        }, 500);
      }, 3500); // Show success for 3.5 seconds
      
    } catch (error) {
      console.error('Contact error:', error);
      setSubmitError('Failed to send. Please try again or email us directly.');
      setIsSubmitting(false);
    }
  };

  const colors = variant === 'terminal' 
    ? { primary: '#22d3ee', secondary: '#06b6d4', text: '#67e8f9' }
    : { primary: '#fbbf24', secondary: '#f59e0b', text: '#fcd34d' };

  return (
    <>
      {/* Floating Orb Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group"
        style={{
          background: isOpen 
            ? `radial-gradient(circle, ${colors.primary}40, ${colors.secondary}20)`
            : `radial-gradient(circle, ${colors.primary}30, ${colors.secondary}10)`,
          border: `2px solid ${colors.primary}50`,
          boxShadow: isOpen
            ? `0 0 30px ${colors.primary}60, 0 8px 16px rgba(0,0,0,0.3)`
            : `0 0 20px ${colors.primary}40, 0 4px 12px rgba(0,0,0,0.2)`,
          transform: isOpen ? 'scale(1.1)' : 'scale(1)'
        }}
        title="Contact M.ai.ry's colleagues"
      >
        {isOpen ? (
          <RemoveXIcon size={20} color={colors.primary} />
        ) : (
          <BelongingHeartIcon size={24} color={colors.primary} className="group-hover:animate-pulse" />
        )}
      </button>

      {/* Contact Form Popup */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-80 rounded-2xl shadow-2xl overflow-hidden"
          style={{
            background: variant === 'terminal' 
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            border: `2px solid ${colors.primary}60`,
            boxShadow: `0 0 40px ${colors.primary}40, 0 8px 24px rgba(0,0,0,0.4)`,
            backdropFilter: 'blur(12px)'
          }}
        >
          {/* Header */}
          <div 
            className="p-4 border-b"
            style={{ 
              borderColor: `${colors.primary}30`,
              background: `linear-gradient(135deg, ${colors.primary}15, ${colors.secondary}10)`
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <BelongingHeartIcon size={18} color={colors.primary} />
              <h3 
                className="font-semibold"
                style={{ 
                  color: colors.text,
                  fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif'
                }}
              >
                Reach Out
              </h3>
            </div>
            <p 
              className="text-xs opacity-70"
              style={{ 
                color: colors.text,
                fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif'
              }}
            >
              One of M.ai.ry's colleagues will contact you
            </p>
          </div>

          {/* Form */}
          {submitted ? (
            <div className="p-8 text-center">
              <div className="mb-4">
                <ConfirmCheckIcon 
                  size={64} 
                  color={colors.primary} 
                  className="mx-auto animate-pulse"
                  style={{
                    filter: `drop-shadow(0 0 20px ${colors.primary}80)`
                  }}
                />
              </div>
              <p 
                className="font-bold text-lg mb-2"
                style={{ color: colors.text, fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif' }}
              >
                Message Sent! ✨
              </p>
              <p 
                className="text-sm opacity-80 mb-1"
                style={{ color: colors.text, fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif' }}
              >
                One of M.ai.ry's colleagues will reach out soon.
              </p>
              {formData.newsletter && (
                <p 
                  className="text-xs opacity-60 mt-2"
                  style={{ color: colors.text }}
                >
                  You're on the newsletter list! 🌸
                </p>
              )}
              <div 
                className="mt-4 text-xs opacity-50"
                style={{ color: colors.text }}
              >
                Closing...
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              {/* Name */}
              <input
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-3 py-2 rounded-lg outline-none text-sm"
                style={{
                  background: variant === 'terminal' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  border: `1px solid ${colors.primary}30`,
                  color: colors.text,
                  fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif'
                }}
              />

              {/* Contact Type Toggle */}
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, contactType: 'email' })}
                  className="px-3 py-1 rounded transition-all"
                  style={{
                    background: formData.contactType === 'email' ? `${colors.primary}30` : 'transparent',
                    border: `1px solid ${colors.primary}40`,
                    color: colors.text
                  }}
                >
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, contactType: 'phone' })}
                  className="px-3 py-1 rounded transition-all"
                  style={{
                    background: formData.contactType === 'phone' ? `${colors.primary}30` : 'transparent',
                    border: `1px solid ${colors.primary}40`,
                    color: colors.text
                  }}
                >
                  Phone
                </button>
              </div>

              {/* Email or Phone */}
              <input
                type={formData.contactType === 'email' ? 'email' : 'tel'}
                placeholder={formData.contactType === 'email' ? 'your@email.com' : 'Your phone number'}
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                required
                className="w-full px-3 py-2 rounded-lg outline-none text-sm"
                style={{
                  background: variant === 'terminal' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  border: `1px solid ${colors.primary}30`,
                  color: colors.text,
                  fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif'
                }}
              />

              {/* Message */}
              <textarea
                placeholder="What's on your mind?"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={3}
                className="w-full px-3 py-2 rounded-lg outline-none text-sm resize-none"
                style={{
                  background: variant === 'terminal' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  border: `1px solid ${colors.primary}30`,
                  color: colors.text,
                  fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif'
                }}
              />

              {/* Newsletter Signup Checkbox */}
              {formData.contactType === 'email' && (
                <label className="flex items-start gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.newsletter}
                    onChange={(e) => setFormData({ ...formData, newsletter: e.target.checked })}
                    className="mt-1"
                    style={{ accentColor: colors.primary }}
                  />
                  <span 
                    className="text-xs opacity-70 group-hover:opacity-90 transition-opacity"
                    style={{ 
                      color: colors.text,
                      fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif'
                    }}
                  >
                    Join the Belonging Sessions community
                  </span>
                </label>
              )}

              {/* Error Message */}
              {submitError && (
                <div 
                  className="text-xs p-2 rounded"
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#f87171'
                  }}
                >
                  {submitError}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || !formData.name || !formData.contact || !formData.message}
                className="w-full py-2.5 rounded-lg font-medium text-sm transition-all"
                style={{
                  background: (isSubmitting || !formData.name || !formData.contact || !formData.message)
                    ? `${colors.secondary}20`
                    : `linear-gradient(135deg, ${colors.primary}50, ${colors.secondary}50)`,
                  border: `1px solid ${colors.primary}60`,
                  color: variant === 'terminal' ? '#000' : '#fff',
                  fontFamily: variant === 'terminal' ? 'monospace' : '"Crimson Text", serif',
                  fontWeight: '600',
                  opacity: (isSubmitting || !formData.name || !formData.contact || !formData.message) ? 0.5 : 1,
                  cursor: (isSubmitting || !formData.name || !formData.contact || !formData.message) ? 'not-allowed' : 'pointer',
                  boxShadow: (formData.name && formData.contact && formData.message && !isSubmitting)
                    ? `0 0 20px ${colors.primary}40`
                    : 'none'
                }}
              >
                {isSubmitting ? '◉ Sending...' : '✓ Send Message'}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default ContactOrb;
